import type { PinduoduoOrderFacts } from './pinduoduo'

export interface PddAfterSalesMetrics {
  applicationCount: number
  uniqueOrderCount: number
  requestedRefundAmount: number
  successfulRefundCount: number
  successfulRefundAmount: number
  pendingCount: number
  closedUnsuccessfulCount: number
  refundOnlyCount: number
  returnRefundCount: number
  exchangeResendCount: number
  shippedCount: number
  unshippedCount: number
  secondReturnOrderCount: number
  qualityIssueCount: number
}

export interface PddAfterSalesDailyRecord extends PddAfterSalesMetrics {
  date: string
}

export interface PddAfterSalesDailyProductRecord extends PddAfterSalesDailyRecord {
  productId: string
  skuExamples: string[]
}

export interface PddAfterSalesDailySkuRecord extends PddAfterSalesDailyRecord {
  productId: string
  variantId: string | null
  skuInformation: string
}

export interface PddAfterSalesDailyReasonRecord extends PddAfterSalesDailyRecord {
  reason: string
}

export interface PddAfterSalesDailyTypeRecord extends PddAfterSalesDailyRecord {
  refundType: string
}

export interface PddAfterSalesDailyCategoryRecord extends PddAfterSalesDailyRecord {
  category: string
}

export interface PddAfterSalesDailyStatusRecord extends PddAfterSalesDailyRecord {
  status: string
}

export interface PddSecondReturnPaymentCohortProductRecord {
  paymentDate: string
  productId: string
  secondReturnOrderCount: number
  secondReturnItemCount: number
}

export function pddSecondReturnCohortByProduct(
  rows: readonly PddSecondReturnPaymentCohortProductRecord[],
  startDate: string,
  endDate: string,
) {
  const result = new Map<string, { secondReturnOrderCount: number; secondReturnItemCount: number }>()
  for (const row of rows) {
    if (row.paymentDate < startDate || row.paymentDate > endDate) continue
    const current = result.get(row.productId) || { secondReturnOrderCount: 0, secondReturnItemCount: 0 }
    current.secondReturnOrderCount += row.secondReturnOrderCount
    current.secondReturnItemCount += row.secondReturnItemCount
    result.set(row.productId, current)
  }
  return result
}

export interface PinduoduoAfterSalesFacts {
  schemaVersion: string
  generatedAt: string
  meta: {
    platformId: 'pinduoduo'
    storeId: string
    storeName: string
    timezone: string
    queryStart: string
    queryEnd: string
    exportedAt: string
    sourceFileName: string
    sourceSha256: string
    sourceRows: number
    sourceColumns: number
    sourceRetained: false
    privacy: string
    droppedSensitiveColumns: string[]
    caliber: string
    skuMapping: {
      method: string
      catalogSourceSha256: string
      mappedSourceRows: number
      unmappedSourceRows: number
      ambiguousSourceRows: number
    }
    paymentCohort: {
      state: 'ready' | 'incomplete' | 'unavailable'
      reason?: string
      sourceCoverageComplete?: boolean
      orderSourceFileName?: string
      orderSourceSha256?: string
      orderSourceRows?: number
      orderHistorySources?: Array<{
        fileName: string
        sha256: string
        rows: number
        paymentQueryStart: string
        paymentQueryEnd: string
      }>
      mergedPaidOrderRows?: number
      paymentQueryStart?: string
      paymentQueryEnd?: string
      observationEnd?: string
      identityRule?: string
      matchedSecondReturnOrders?: number
      unmatchedSecondReturnOrders?: number
      productMismatchOrders?: number
    }
  }
  summary: PddAfterSalesMetrics & { productCount: number }
  daily: PddAfterSalesDailyRecord[]
  dailyProducts: PddAfterSalesDailyProductRecord[]
  dailySkus: PddAfterSalesDailySkuRecord[]
  dailyReasons: PddAfterSalesDailyReasonRecord[]
  dailyTypes: PddAfterSalesDailyTypeRecord[]
  dailyCategories: PddAfterSalesDailyCategoryRecord[]
  dailyStatuses: PddAfterSalesDailyStatusRecord[]
  paymentCohortProducts: PddSecondReturnPaymentCohortProductRecord[]
}

const metricKeys: Array<keyof PddAfterSalesMetrics> = [
  'applicationCount', 'uniqueOrderCount', 'requestedRefundAmount', 'successfulRefundCount',
  'successfulRefundAmount', 'pendingCount', 'closedUnsuccessfulCount', 'refundOnlyCount',
  'returnRefundCount', 'exchangeResendCount', 'shippedCount', 'unshippedCount', 'secondReturnOrderCount', 'qualityIssueCount',
]

export const emptyPddAfterSalesMetrics = (): PddAfterSalesMetrics => ({
  applicationCount: 0,
  uniqueOrderCount: 0,
  requestedRefundAmount: 0,
  successfulRefundCount: 0,
  successfulRefundAmount: 0,
  pendingCount: 0,
  closedUnsuccessfulCount: 0,
  refundOnlyCount: 0,
  returnRefundCount: 0,
  exchangeResendCount: 0,
  shippedCount: 0,
  unshippedCount: 0,
  secondReturnOrderCount: 0,
  qualityIssueCount: 0,
})

export function sumPddAfterSalesMetrics(rows: PddAfterSalesMetrics[]): PddAfterSalesMetrics {
  const result = emptyPddAfterSalesMetrics()
  for (const row of rows) {
    for (const key of metricKeys) result[key] += row[key]
  }
  return result
}

/** Missing coverage metadata is not proof that a historical payment cohort is complete. */
export function pddPaymentCohortIssue(
  cohort: PinduoduoAfterSalesFacts['meta']['paymentCohort'] | undefined,
  orderSourceSha256: string | undefined,
): string | null {
  if (!cohort || cohort.state === 'unavailable') return '支付订单队列尚未就绪，暂不展示二退率'
  const unmatched = cohort.unmatchedSecondReturnOrders
  const mismatched = cohort.productMismatchOrders
  if (!Number.isInteger(unmatched) || !Number.isInteger(mismatched) || unmatched! < 0 || mismatched! < 0) {
    return '缺少订单关联完整性校验，暂不展示二退率'
  }
  if (unmatched! > 0 || mismatched! > 0) {
    return `二退订单关联不完整（未匹配 ${unmatched} 单、商品不一致 ${mismatched} 单），暂不展示二退率`
  }
  if (cohort.sourceCoverageComplete !== true) return '支付订单全状态数据未齐，暂不展示二退率'
  if (cohort.state !== 'ready') return '支付订单队列不完整，暂不展示二退率'
  if (!orderSourceSha256 || cohort.orderSourceSha256 !== orderSourceSha256) {
    return '售后与订单数据批次不一致，暂不展示二退率'
  }
  return null
}

export function pddPaidOrderCoverageIssue(
  meta: PinduoduoOrderFacts['meta'] | undefined,
  startDate: string,
  endDate: string,
): string | null {
  const outcomes = ['confirmed', 'refunded', 'after_sales_pending'] as const
  if (!meta || !startDate || !endDate || outcomes.some((outcome) => !meta.outcomeSources?.[outcome])) {
    return '缺少下单件数完整性校验，暂不展示二退率'
  }
  if (outcomes.some((outcome) => {
    const source = meta.outcomeSources![outcome]!
    return !source.queryStart || !source.queryEnd || source.queryStart > startDate || source.queryEnd < endDate
  })) return '所选日期内退款及成交订单数据未齐，暂不展示二退率'
  return null
}

/** User-approved reference: omit unmatched returns; keep identity and date guards. */
export function pddReferenceCohortIssue(
  cohort: PinduoduoAfterSalesFacts['meta']['paymentCohort'] | undefined,
  meta: PinduoduoOrderFacts['meta'] | undefined,
  startDate: string,
  endDate: string,
): string | null {
  if (!cohort || cohort.state === 'unavailable') return '支付订单队列尚未就绪'
  if (!meta?.sourceSha256 || cohort.orderSourceSha256 !== meta.sourceSha256) return '售后与订单数据批次不一致'
  if (!Number.isInteger(cohort.unmatchedSecondReturnOrders) || cohort.unmatchedSecondReturnOrders! < 0
    || cohort.productMismatchOrders !== 0) return '订单关联校验异常'
  const history = meta.reconciliation
  if (!history?.reconstructedPaymentStart || !history.observationEnd
    || !startDate || !endDate || startDate < history.reconstructedPaymentStart || endDate > history.observationEnd) {
    return pddPaymentCohortIssue(cohort, meta.sourceSha256) || pddPaidOrderCoverageIssue(meta, startDate, endDate)
  }
  return null
}
